import { Typography, Radio, Button, Alert, Spin, Space } from 'antd'
import { CheckCircleFilled, PlusOutlined } from '@ant-design/icons'
import { useGetAllWorkspaces, useJoinWorkspace, useCreateWorkspaceModal } from '~/app/services/workspace'
import { useAuthStore } from '~/app/services/auth'
import { useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { AUTH_PATH } from '~/app/lib/paths'
import AuthLayout from '~/app/components/layouts/AuthLayout'
import CreateWorkspaceModal from '~/app/components/utils/Modals/CreateWorkspaceModal'

const { Title, Text } = Typography

export default function WorkspaceSelectionPage() {
  const navigate = useNavigate()
  const { accessToken, selectedTenant } = useAuthStore()
  const { data: workspaces, isLoading, error } = useGetAllWorkspaces()
  const { mutate: joinWorkspace, isPending } = useJoinWorkspace()
  const { setIsOpen: setCreateModalOpen } = useCreateWorkspaceModal()
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(null)

  useEffect(() => {
    // Redirect to login if no auth
    if (!accessToken || !selectedTenant) {
      navigate(AUTH_PATH.SIGN_IN)
    }
  }, [accessToken, selectedTenant, navigate])

  useEffect(() => {
    // Log workspace data for debugging
    if (workspaces) {
      console.log('📋 Workspaces loaded for tenant:', selectedTenant?.name, '- Count:', workspaces.length)
      console.log('📋 Workspace IDs:', workspaces.map(w => ({ id: w.id, name: w.name })))
    }
  }, [workspaces, selectedTenant])

  const handleConnectWorkspace = () => {
    if (selectedWorkspaceId) {
      joinWorkspace(selectedWorkspaceId)
    }
  }

  if (isLoading) {
    return (
      <AuthLayout>
        <div className="flex items-center justify-center">
          <Spin size="large" tip="Loading workspaces..." />
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <Title level={2} className="mb-2">
            👋 Welcome back, {selectedTenant?.name}!
          </Title>
        </div>

        {error && (
          <Alert
            message="Error Loading Workspaces"
            description={error.message || 'Failed to load workspaces'}
            type="error"
            showIcon
            className="mb-4"
          />
        )}

        <div className="bg-white rounded-lg p-6 shadow-sm">
          <Text strong className="block mb-4 text-base">
            Choose workspace
          </Text>

          <Radio.Group
            value={selectedWorkspaceId}
            onChange={(e) => setSelectedWorkspaceId(e.target.value)}
            className="w-full"
          >
            <Space direction="vertical" className="w-full" size="middle">
              {workspaces?.map((workspace) => (
                <div
                  key={workspace.id}
                  className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all cursor-pointer ${
                    selectedWorkspaceId === workspace.id
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedWorkspaceId(workspace.id)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-orange-400 flex items-center justify-center text-white text-xl font-bold">
                      {workspace.avatar ? (
                        <img src={workspace.avatar} alt={workspace.name} className="w-full h-full rounded-lg" />
                      ) : (
                        workspace.name.charAt(0).toUpperCase()
                      )}
                    </div>
                    <div>
                      <Text strong className="block text-base">
                        {workspace.name}
                      </Text>
                    </div>
                  </div>
                  {selectedWorkspaceId === workspace.id && (
                    <CheckCircleFilled className="text-green-500 text-2xl" />
                  )}
                </div>
              ))}

              <div
                className="flex items-center gap-3 p-4 rounded-lg border-2 border-dashed border-gray-300 hover:border-green-500 transition-all cursor-pointer"
                onClick={() => setCreateModalOpen(true)}
              >
                <PlusOutlined className="text-green-600 text-xl" />
                <Text strong className="text-green-600">
                  Create new Workspace
                </Text>
              </div>
            </Space>
          </Radio.Group>

          <div className="mt-6 flex justify-center">
            <Button
              type="primary"
              size="large"
              onClick={handleConnectWorkspace}
              disabled={!selectedWorkspaceId}
              loading={isPending}
              className="px-12"
              style={{ 
                backgroundColor: '#059669',
                borderColor: '#059669',
                height: '48px',
                fontSize: '16px'
              }}
            >
              Connect with workspace
            </Button>
          </div>
        </div>
      </div>

      <CreateWorkspaceModal />
    </AuthLayout>
  )
}
